import './Skeleton.scss'
import type { CSSProperties } from 'react'
import { clx } from '../clx'

export interface SkeletonProps {
  variant?: 'text' | 'rect' | 'circle'
  /** Number of text lines (text variant only). The last of several is shorter. */
  lines?: number
  /** Width hint: a percentage or a relative CSS length ('60%', '8rem', '12ch'). Never px. */
  width?: string
  /** Height hint for `rect` (and the diameter for `circle`, when no width is given). */
  height?: string
  className?: string
}

type SkeletonStyle = CSSProperties & Record<`--skeleton-${string}`, string>

/**
 * A decorative loading placeholder (always aria-hidden: announce loading on
 * the container instead, e.g. aria-busy). The shimmer stops under reduced motion.
 */
export function Skeleton({ variant = 'text', lines = 1, width, height, className }: SkeletonProps) {
  const style: SkeletonStyle = {}
  if (width) style['--skeleton-width'] = width
  if (height) style['--skeleton-height'] = height

  const classes = clx('skeleton', `skeleton--as-${variant}`, className)

  if (variant === 'text' && lines > 1) {
    return (
      <span className={clx(classes, 'skeleton--has-lines')} style={style} aria-hidden="true">
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="skeleton__line" />
        ))}
      </span>
    )
  }

  return <span className={classes} style={style} aria-hidden="true" />
}
